import type { DesiredOutcome } from '~/models/project'
import type { CalendarDate, IsoTimestamp } from '~/types/dates'

/**
 * Everything the route engine is allowed to see.
 *
 * The same contract as `RulesInputSnapshot`: no clock, no storage, no settings
 * lookup, and the whole input is one value so that the same snapshot and the
 * same thresholds always produce the same recommendation.
 *
 * Collection happens outside `rules/` — in the wizard at sub-checkpoint 2.5c,
 * which holds these answers in memory before anything is written. That is not a
 * detail of convenience: the layering guard forbids `rules/` from importing
 * storage, and setup has nothing stored to read anyway.
 *
 * **Every field here is consumed.** D-062's converse of D-060 applies: do not
 * add a lifestyle or profile field the engine does not read. Activity level and
 * sleep quality are absent for exactly that reason.
 */
export interface RouteInputSnapshot {
  /** The date the project would start. Injected, never read from a clock. */
  readonly reference: CalendarDate
  /** When the snapshot was taken. Carried for the audit trail only. */
  readonly evaluatedAt: IsoTimestamp
  /** The destination the user described. */
  readonly desiredOutcome: DesiredOutcome
  /** The date they want to be done by. Required — the project is bounded. */
  readonly targetDate: CalendarDate
  /** Median of three readings, in centimetres (D-045). */
  readonly waistCm: number | undefined
  readonly heightCm: number | undefined
  /**
   * The Navy estimate, where it could be computed at all.
   *
   * Optional by design: `waist <= neck` and the other D-014 invalid states
   * produce no estimate, and that does not block a route (D-071).
   */
  readonly estimatedBodyFatPercent: number | undefined
  /**
   * Realistic weekly training sessions.
   *
   * Read by the lean-bulk floor (D-071), which is what justifies asking for it
   * at all.
   */
  readonly weeklySessions: number | undefined
  /**
   * Self-reported training experience.
   *
   * **Changes the stated aim, never the route** (D-071). Routing stays driven
   * by measured condition rather than a label the user picks about themselves,
   * and shifting the 0.50 boundary for beginners was considered and rejected —
   * without recomposition as a selectable route, recommending maintenance to a
   * novice at 0.52 would be planning a recomposition by implication.
   */
  readonly trainingExperience: TrainingExperience | undefined
  /**
   * The event, when the outcome is `prepareForEvent`.
   *
   * Distinct from `targetDate`: the event is the fixed thing the preparation
   * runs into, and D-071 gates the route on how many weeks away it is.
   */
  readonly eventDate: CalendarDate | undefined
}

export const TRAINING_EXPERIENCE_LEVELS = ['beginner', 'intermediate', 'advanced'] as const

export type TrainingExperience = (typeof TRAINING_EXPERIENCE_LEVELS)[number]

export function isTrainingExperience(value: string): value is TrainingExperience {
  return (TRAINING_EXPERIENCE_LEVELS as readonly string[]).includes(value)
}

/** Display labels. Presentation only — never persisted, never compared. */
export const TRAINING_EXPERIENCE_LABELS: Readonly<Record<TrainingExperience, string>> = {
  beginner: 'Less than a year of consistent training',
  intermediate: 'One to three years',
  advanced: 'More than three years',
}
